import React, { useMemo, useState } from "react";
import { Heart } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { toast } from "sonner";

/**
 * Toggleable like button for a feed post.
 * Optimistic: flips immediately, reconciles with server response.
 */
export default function LikeButton({ post, onChange }) {
  const { user } = useAuth();
  const { t } = useT();
  const initialLikes = useMemo(() => post?.likes || [], [post]);
  const [liked, setLiked] = useState(!!(user && initialLikes.includes(user.id)));
  const [count, setCount] = useState(initialLikes.length);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (!user || user === false) {
      toast.error(t("err.unauthorized"));
      return;
    }
    if (busy) return;
    setBusy(true);
    // Optimistic
    const nextLiked = !liked;
    const nextCount = count + (nextLiked ? 1 : -1);
    setLiked(nextLiked);
    setCount(Math.max(0, nextCount));
    try {
      const { data } = await api.post(`/feed/${post.id}/like`);
      setLiked(!!data.liked);
      setCount(Number(data.count) || 0);
      if (typeof onChange === "function") onChange({ liked: data.liked, count: data.count });
    } catch (e) {
      // revert
      setLiked(!nextLiked);
      setCount(Math.max(0, count));
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy || !user || user === false}
      data-testid={`like-btn-${post.id}`}
      aria-pressed={liked}
      aria-label={liked ? t("feed.liked") : t("feed.like")}
      className={`inline-flex items-center gap-1.5 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all ${
        liked
          ? "bg-[#E11D48] text-white border-[#E11D48] shadow-[0_0_12px_rgba(225,29,72,0.35)]"
          : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:border-[#E11D48] hover:text-[#E11D48]"
      } disabled:opacity-50`}
    >
      <Heart className={`w-3.5 h-3.5 ${liked ? "fill-white" : ""}`} />
      <span data-testid={`like-count-${post.id}`}>{count}</span>
    </button>
  );
}
